import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle, Plus, Edit, Trash2, Loader2, RefreshCw, X } from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import BaseModal from '@framework/components/BaseModal';
import ActionButton from '@framework/components/ActionButton';
import ErrorAlert from '@framework/components/ErrorAlert';
import { parseApiDate } from '@framework/utils/dateUtils';

const KEY_PATTERN = /^[a-z0-9_]+$/;
const MAX_KEYWORDS = 50;
const EMPTY_FORM = { key: '', title: '', content: '', keywords: [] };

const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent';

function formatDatetime(isoString) {
  const d = parseApiDate(isoString);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function HelpArticlesPage() {
  const { apiService: api } = useTheme();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // ---- Data loading ----

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getHelpArticles();
      setArticles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load help articles:', err);
      setError('Failed to load help articles');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // ---- Modal helpers ----

  const openCreate = () => {
    setEditingKey(null);
    setForm({ ...EMPTY_FORM });
    setKeywordInput('');
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = async (row) => {
    setEditingKey(row.key);
    setForm({ key: row.key, title: '', content: '', keywords: [] });
    setKeywordInput('');
    setFormError(null);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const full = await api.getHelpArticle(row.key);
      setForm({
        key: full.key,
        title: full.title || '',
        content: full.content || '',
        keywords: full.keywords || [],
      });
    } catch (err) {
      console.error('Failed to load help article:', err);
      setFormError('Failed to load article');
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingKey(null);
    setFormError(null);
  };

  // ---- Form field helpers ----

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw || form.keywords.includes(kw) || form.keywords.length >= MAX_KEYWORDS) return;
    setForm((prev) => ({ ...prev, keywords: [...prev.keywords, kw] }));
    setKeywordInput('');
  };

  const removeKeyword = (kw) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((k) => k !== kw) }));
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    }
  };

  // ---- Save ----

  const handleSave = async () => {
    if (!editingKey && !KEY_PATTERN.test(form.key)) {
      setFormError('Key must contain only lowercase letters, numbers, and underscores');
      return;
    }
    if (!form.title.trim()) {
      setFormError('Title is required');
      return;
    }
    if (!form.content.trim()) {
      setFormError('Content is required');
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      if (editingKey) {
        await api.updateHelpArticle(editingKey, {
          title: form.title.trim(),
          content: form.content.trim(),
          keywords: form.keywords,
        });
      } else {
        await api.createHelpArticle({
          key: form.key.trim(),
          title: form.title.trim(),
          content: form.content.trim(),
          keywords: form.keywords,
        });
      }

      closeModal();
      await loadArticles();
    } catch (err) {
      console.error('Failed to save help article:', err);
      setFormError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete ----

  const handleDelete = async (row) => {
    if (!confirm(`Delete help article "${row.title}" (${row.key})?`)) return;
    try {
      await api.deleteHelpArticle(row.key);
      await loadArticles();
    } catch (err) {
      console.error('Failed to delete help article:', err);
      setError('Failed to delete help article');
    }
  };

  // ---- Loading state ----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // ---- Main render ----

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <HelpCircle className="w-6 h-6 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Help Articles</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">({articles.length})</span>
        </div>
        <div className="flex items-center space-x-2">
          <ActionButton icon={RefreshCw} variant="ghost" size="sm" onClick={loadArticles}>
            Refresh
          </ActionButton>
          <ActionButton icon={Plus} variant="primary" size="sm" onClick={openCreate}>
            New Article
          </ActionButton>
        </div>
      </div>

      <ErrorAlert error={error} onClose={() => setError(null)} />

      {articles.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <HelpCircle className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No help articles yet.</p>
          <ActionButton icon={Plus} variant="primary" size="sm" className="mt-4" onClick={openCreate}>
            Create your first article
          </ActionButton>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Key</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Keywords</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {articles.map((row) => (
                  <tr key={row.key} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                    <td className="px-4 py-3">
                      <code className="text-xs font-mono text-gray-600 dark:text-gray-400">{row.key}</code>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{row.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{row.keyword_count}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDatetime(row.updated_at)}
                      {row.updated_by && <div className="text-gray-400 dark:text-gray-500">by {row.updated_by}</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <ActionButton icon={Edit} variant="ghost" size="xs" onClick={() => openEdit(row)} title="Edit" />
                        <ActionButton icon={Trash2} variant="ghost" size="xs" onClick={() => handleDelete(row)} title="Delete" className="text-red-500 hover:text-red-700" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <BaseModal isOpen={modalOpen} onClose={closeModal} title={editingKey ? `Edit "${editingKey}"` : 'New Help Article'} size="lg">
        <div className="p-6 space-y-5">
          <ErrorAlert error={formError} onClose={() => setFormError(null)} />

          {modalLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Key <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.key}
                  onChange={(e) => updateField('key', e.target.value)}
                  className={`${inputClass} font-mono ${editingKey ? 'opacity-60 cursor-not-allowed' : ''}`}
                  placeholder="e.g. pantry_management"
                  disabled={!!editingKey}
                />
                {!editingKey && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Lowercase letters, numbers, and underscores only. Cannot be changed after creation.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={inputClass}
                  placeholder="Article title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => updateField('content', e.target.value)}
                  rows={12}
                  className={`${inputClass} font-mono text-xs resize-y`}
                  placeholder="Markdown content shown to Zenie chat users"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Keywords</label>
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                  Phrases Zenie matches against user questions to find this article.
                </p>
                {form.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.keywords.map((kw) => (
                      <span
                        key={kw}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >
                        {kw}
                        <button onClick={() => removeKeyword(kw)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    className={inputClass}
                    placeholder="Type a keyword and press Enter"
                    disabled={form.keywords.length >= MAX_KEYWORDS}
                  />
                  <ActionButton variant="secondary" size="md" onClick={addKeyword} disabled={form.keywords.length >= MAX_KEYWORDS}>
                    Add
                  </ActionButton>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <ActionButton variant="secondary" size="md" onClick={closeModal} disabled={saving}>
                  Cancel
                </ActionButton>
                <ActionButton variant="primary" size="md" onClick={handleSave} loading={saving}>
                  {editingKey ? 'Save Changes' : 'Create'}
                </ActionButton>
              </div>
            </>
          )}
        </div>
      </BaseModal>
    </div>
  );
}
